import GenericCrudTable from "@/components/tables/genericCrudTable";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEFAULTPICKERDATE } from "@/types/Entities";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { useEffect, useRef, useState } from "react";
import { IDropDownOption } from "@/types/DropDown";
import RestrictedPage from "@/consumer/restrictedPage";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import styles from "@/routes/admin/appointmentList.module.css";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import { api } from "@/helper/api";
import { useDispatch } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import SelectedSubjectDisplay from "@/components/general/selectedSubjectDisplay";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import AppointmentBatchCreateModal from "@/components/appointment/appointmentBatchCreateModal";
import { replaceDate } from "@/helper/dateHandling";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import {
  DEFAULT_KAER_TEAM_ID,
  KAER_COMPANY_SUBJECT_ID,
} from "@/helper/systemIds";
import { appointmentEntityAdapter } from "@/components/appointment/appointmentEntityAdapter";

export default function AppointmentList() {
  /*eslint-disable */
  const [options, setOptions] = useState<IDropDownOption[]>([]);
  /*eslint-enable */
  const [subjectId, setSubjectId] = useState<string>("");
  const [urlSubjectName, setUrlSubjectName] = useState<string>("");
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const { subjectId: urlSubjectId } = useParams<{ subjectId?: string }>();

  const statusFilterOptions = getTranslatedStatusOptions();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();
  usePageHeading(t("appointments_booked"));
  const appointmentRef = useRef(null);

  useEffect(() => {
    if (!urlSubjectId) return;
    setSubjectId(urlSubjectId);
    api.genericApiRequest({
      method: "get",
      entity: "subject",
      entityId: urlSubjectId,
      successHandler: (res: any) => {
        setUrlSubjectName(res.data?.name || "");
      },
      failHandler: () => {
        setUrlSubjectName("");
      },
    });
  }, [urlSubjectId]);

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubjectId(selectedOption.value);
    }
  };

  const resetAsyncInput = () => {
    setSubjectId("");
  };

  const handleResetToSubjectSearch = () => {
    dispatch(setSubjectSearch({}));
    setUrlSubjectName("");
    setSubjectId("");
    navigate("/appointmentList");
  };

  const handleNavigateAfterPost = (data: any) => {
    const editUrl = `/settings/appointmentEditor/${data.id}`;
    navigate(editUrl);
  };

  const handleBatchCreateSuccess = () => {
    setRefreshTable((prev) => !prev);
  };

  const batchCreateButton = (
    <GenericButton
      startIcon={<PlaylistAddIcon />}
      onClick={() => setIsBatchModalOpen(true)}
    >
      {t("add_multiple")}
    </GenericButton>
  );

  return (
    <RestrictedPage permissionRequired="frontend_view_appointment">
      {urlSubjectId ? (
        <SelectedSubjectDisplay
          wrapperClassName={styles.dropDownSubjectSearch}
          subjectName={urlSubjectName}
          onReset={handleResetToSubjectSearch}
        />
      ) : (
        <div className={styles.dropDownSubjectSearch}>
          <FormFieldLabel
            label={t("subject")}
            htmlFor="appointment-list-subject"
          />
          <DropDownSubjectSearch
            name="appointment-list-subject"
            options={options}
            onChange={handleSubjectSelect}
            subdomain={subdomain}
            isClearable={true}
            resetInput={resetAsyncInput}
            autofocus={true}
            customRef={appointmentRef}
            useGlobalState={true}
          />
        </div>
      )}
      <div data-testid="appointment-list-table">
        <GenericCrudTable
          refreshTable={refreshTable}
          enableRowSelection={true}
          enableBulkDelete={true}
          allowColumnFiltering={true}
          showColumnFilterbyDefault={true}
          withSubjectSearch={true}
          searchId={subjectId}
          entity={"appointment"}
          entityAdapter={appointmentEntityAdapter}
          subjectIdForBuildings={subjectId}
          customCreatePresetValues={{
            team: DEFAULT_KAER_TEAM_ID,
            on_site: true,
          }}
          onSuccessfullPostCallback={handleNavigateAfterPost}
          customTopToolBarActionButton={batchCreateButton}
          allowOpenEditInNewTab
          customEditHandler={(row: any) => {
            const editUrl = `/settings/appointmentEditor/${row.original.id}`;

            if (row.allowOpenInNewTab) {
              window.open(editUrl, "_blank", "noopener,noreferrer");
            } else {
              navigate(editUrl);
            }
          }}
          additionalUrlParameter={{
            include_draft: "true",
            expand: "building_information",
          }}
          expandedDepth={false}
          columns={[
            { header: t("id"), accessorKey: "id", id: "id" },

            {
              header: t("datetime"),
              accessorKey: "datetime",
              id: "datetime",
              enableForCreate: true,
              dateTimePicker: true,
              sortingFn: "datetime",
              defaultDate: DEFAULTPICKERDATE.TODAY,
              required: true,
              errorMessage: t("field_required"),
              filterVariant: "date",
              accessorFn: (row: any) => {
                const appointmentId = row.id;
                const status = row.status;

                return (
                  <>
                    {status !== "draft" ? (
                      <Link
                        to={`/appointmentDashboard/${appointmentId}`}
                        style={{ textDecoration: "none", color: "#8c1ec8" }}
                        target="_blank"
                      >
                        <span>{replaceDate(row.datetime, true)}</span>
                      </Link>
                    ) : (
                      <>
                        <span>{replaceDate(row.datetime, true)}</span>
                      </>
                    )}
                  </>
                );
              },
            },

            {
              header: t("subject"),
              accessorKey: "subject.name",
              id: "subject",
              enableForCreate: true,
              asyncEntity: "subject",
              asyncDropDown: true,
              required: true,
              customColumnFilterId: "subject_name",
              customSortingKey: "subject_name",
              errorMessage: t("field_required"),
              accessorFn: (row: any) => {
                if (row.subject && row.subject.name && row.subject.id) {
                  return (
                    <NavigateToSubjectOverviewLink
                      subjectId={row.subject.id}
                      label={row.subject.name}
                    />
                  );
                }
                return null;
              },
            },
            {
              header: t("company"),
              accessorKey: "subject.company.name",
              id: "company",
              customColumnFilterId: "subject_company_name",
              customSortingKey: "company_name",
              accessorFn: (row: any) => {
                if (
                  row.subject &&
                  row.subject.company &&
                  row.subject.company.name &&
                  row.subject.id
                ) {
                  return (
                    <NavigateToSubjectOverviewLink
                      subjectId={row.subject.id}
                      label={row.subject.company.name}
                    />
                  );
                }
                return null;
              },
            },
            {
              header: t("category"),
              accessorKey: "category.name",
              id: "category",
              customColumnFilterId: "category__name",
              enableForCreate: true,
              entity: "mainCategory",
              required: true,
              errorMessage: t("field_required"),
            },
            {
              header: t("examinations"),
              id: "examination_set",
              accessorKey: "examination_set",
              customColumnFilterId: "examination_set__name",
              customSortingKey: "examination_name",
              accessorFn: (row: any) => {
                const examinations: any = [];
                if (row.examination_set) {
                  for (const key in row.examination_set) {
                    if (row.examination_set[key].name) {
                      examinations.push(row.examination_set[key].name);
                    }
                  }
                }
                return examinations.join(", ");
              },
            },
            {
              header: t("team"),
              accessorKey: "team",
              id: "team",
              enableForCreate: true,
              entity: "team",
              required: true,
              customColumnFilterId: "team__name",
              errorMessage: t("field_required"),
              accessorFn: (row: any) => {
                const name = row.team.name;
                return name;
              },
            },
            {
              header: t("status"),
              accessorKey: "status",
              id: "status",
              customFilterParameter: "__icontains",
              filterVariant: "select",
              filterSelectOptions: statusFilterOptions,
              accessorFn: (row: any) => {
                const status = row.status;
                const string = "status_" + status;

                return `${t(string)} (${status})`;
              },
            },
            {
              header: t("type_kind"),
              customColumnFilterId: "created_from_appointment_request",
              customSortingKey: "created_from_appointment_request",
              filterVariant: "select",
              filterSelectOptions: [
                { value: "true", label: t("appointmentrequest_single") },
                { value: "false", label: t("appointment_manual") },
              ],
              setNoFilterParameter: true,
              accessorFn: (row: any) => {
                const appointmentRequestId = row.appointment_request_id;
                if (appointmentRequestId == null) {
                  return <span>{t("appointment_manual")}</span>;
                } else
                  return (
                    <Link
                      to={`/editappointmentrequest/${appointmentRequestId}/building`}
                      className={styles.link}
                    >
                      {t("appointmentrequest_single")}
                    </Link>
                  );
              },
            },
            {
              header: t("on_site"),
              id: "on_site",
              filterVariant: "select",

              filterSelectOptions: [
                { value: "true", label: t("yes") },
                { value: "false", label: t("no") },
              ],
              setNoFilterParameter: true,
              accessorFn: (row: any) => {
                if (row.on_site) {
                  return t("yes");
                } else return t("no");
              },
              enableForCreate: true,
              required: true,
              errorMessage: t("field_required"),

              selectOptions: [
                { value: false, label: t("no") },
                { value: true, label: t("yes") },
              ],
            },
            {
              header: t("created_on"),
              accessorKey: "created_on",
              id: "created_on",
              sortingFn: "datetime",
              filterVariant: "date",

              Cell: ({ cell }: any) => {
                return <span>{replaceDate(cell.getValue(), true)}</span>;
              },
            },
            {
              header: t("created_by"),
              accessorKey: "created_by",
              id: "created_by",
              customColumnFilterId: "created_by_name",
              Cell: ({ cell }: any) => {
                return <span>{cell.getValue()?.name}</span>;
              },
            },
            {
              header: t("modified_on"),
              accessorKey: "modified_on",
              id: "modified_on",
              sortingFn: "datetime",
              filterVariant: "date",

              Cell: ({ cell }: any) => {
                return <span>{replaceDate(cell.getValue(), true)}</span>;
              },
            },
            {
              header: t("modified_by"),
              accessorKey: "modified_by",
              customColumnFilterId: "modified_by_name",
              id: "modified_by",
              Cell: ({ cell }: any) => {
                return <span>{cell.getValue()?.name}</span>;
              },
            },
            {
              header: t("executed_by"),
              accessorKey: "executed_by",
              customColumnFilterId: "executed_by_name",
              customSortingKey: "executed_by_name",
              id: "executed_by",
              getOnlyActiveUsers: {
                status: true,
                companySubjectId: KAER_COMPANY_SUBJECT_ID,
              },
              enableForCreate: true,
              entity: "user",
              userEntityValueField: "subject_id",
              accessorFn: (row: any) => {
                if (row.executed_by && row.executed_by !== null) {
                  return row.executed_by.name;
                } else return "-";
              },
            },
            {
              header: t("assisted_by"),
              accessorKey: "assisted_by",
              customColumnFilterId: "assisted_by_name",
              customSortingKey: "assisted_by_name",
              id: "assisted_by",
              getOnlyActiveUsers: {
                status: true,
                companySubjectId: KAER_COMPANY_SUBJECT_ID,
              },
              enableForCreate: true,
              entity: "user",
              userEntityValueField: "subject_id",
              accessorFn: (row: any) => {
                if (row.assisted_by && row.assisted_by !== null) {
                  return row.assisted_by.name;
                } else return "-";
              },
            },

            {
              header: t("buildings"),
              entity: "building",
              childEntityName: "building_information",
              accessorKey: "building_information.building",
              customColumnFilterId: "building_information__building__name",
              customSortingKey: "building_name",
              id: "building",
              accessorFn: (row: any) => {
                const { name, street_house, zip, city } =
                  row.building_information;
                const locationParts = [];

                if (name) locationParts.push(name);
                if (street_house) locationParts.push(street_house);

                if (zip || city) {
                  const combined = [zip, city]
                    .filter((part) => part !== null)
                    .join(" ");
                  locationParts.push(combined);
                }

                return locationParts.join(", ");
              },
            },
            {
              header: t("building_contact_name"),
              childEntityName: "building_information",
              accessorKey: "building_information.contact_name",
              customColumnFilterId: "building_information__contact_name",
              customSortingKey: "contact_name",
              id: "name",
              hideField: true,
            },
            {
              header: t("building_contact_email"),
              childEntityName: "building_information",
              accessorKey: "building_information.contact_email",
              id: "email",
              hideField: true,
              customColumnFilterId: "building_information__contact_email",
              customSortingKey: "contact_email",
            },
            {
              header: t("building_contact_phone"),
              childEntityName: "building_information",
              accessorKey: "building_information.contact_phone",
              customColumnFilterId: "building_information__contact_phone",
              customSortingKey: "contact_phone",
              id: "phone",
              hideField: true,
            },
            {
              header: t("building_parking_situation"),
              childEntityName: "building_information",
              customColumnFilterId: "building_information__parking_situation",
              accessorKey: "building_information.parking_situation",
              customSortingKey: "parking_situation",
              id: "parking_situation",
              hideField: true,
            },
            {
              header: t("building_additional_information"),
              childEntityName: "building_information",
              customColumnFilterId:
                "building_information__additional_information",
              accessorKey: "building_information.additional_information",
              customSortingKey: "additional_information",
              id: "additional_information",
              hideField: true,
            },
          ]}
          customColumnVisibility={{
            name: false,
            email: false,
            phone: false,
            parking_situation: false,
            additional_information: false,
          }}
        />
      </div>
      <AppointmentBatchCreateModal
        open={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        onSuccess={handleBatchCreateSuccess}
      />
    </RestrictedPage>
  );
}
